import React, { useState } from "react"
import * as api from "../api"
import * as v2layouts from "../layouts"

interface ConfigurationModalProps {
	asset: api.AssetExtract
	onClose: () => void
	onSave: (config: api.AssetConfigureRequest) => Promise<void>
}

export function ConfigurationModal({ asset, onClose, onSave }: ConfigurationModalProps): JSX.Element {
	const parseContentText = (): { description: string; rule: string } => {
		const contentText = asset.content_text || ""
		const colonIndex = contentText.indexOf(":")

		if (colonIndex !== -1) {
			return {
				description: contentText.substring(0, colonIndex).trim(),
				rule: contentText.substring(colonIndex + 1).trim(),
			}
		}

		return { description: "", rule: contentText }
	}

	const parsedContent = parseContentText()

	const [configuredAs, setConfiguredAs] = useState(asset.configured_as || "")
	const [config, setConfig] = useState<api.AssetConfigureRequest>({
		...api.zeros.assets.configureRequestZero(),
		description: parsedContent.description,
		rule: parsedContent.rule,
	})
	const [saving, setSaving] = useState(false)

	const handleSave = async (): Promise<void> => {
		setSaving(true)
		const configToSend = { ...config, configured_as: configuredAs }
		try {
			await onSave(configToSend)
			onClose()
		} catch (err) {
			console.error("failed to save configuration", err)
			alert("Failed to save configuration")
		} finally {
			setSaving(false)
		}
	}

	const mimetype = (asset.mimetype || "").toLowerCase()
	const asset_type = (asset.asset_type || "").toLowerCase()

	const is_font_file = mimetype.includes("otf") || mimetype.includes("ttf")

	return (
		<v2layouts.Modal onClick={onClose}>
			<v2layouts.ModalContent onClick={(e) => e.stopPropagation()}>
				<v2layouts.ModalTitle>Configure Asset</v2layouts.ModalTitle>

				<v2layouts.FormGroup>
					<v2layouts.FormLabel>Asset Type</v2layouts.FormLabel>
					<v2layouts.Select value={configuredAs} onChange={(e) => setConfiguredAs(e.target.value)}>
						<option value="">Select type...</option>
						{asset_type === "image" && (
							<>
								{is_font_file ? (
									<option value="font">Font</option>
								) : (
									<>
										<option value="logo">Logo</option>
									</>
								)}
							</>
						)}
						{asset_type === "text" && (
							<>
								<option value="lookandfeel_rule">Look and Feel Rule</option>
								<option value="tone_of_voice">Tone of Voice</option>
							</>
						)}
					</v2layouts.Select>
				</v2layouts.FormGroup>

				{configuredAs === "logo" && <LogoConfiguration config={config} onChange={setConfig} />}
				{configuredAs === "font" && <FontConfiguration config={config} onChange={setConfig} asset={asset} />}
				{configuredAs === "lookandfeel_rule" && <LookAndFeelConfiguration config={config} onChange={setConfig} />}
				{configuredAs === "tone_of_voice" && <ToneOfVoiceConfiguration config={config} onChange={setConfig} />}

				<v2layouts.ButtonGroup style={{ marginTop: "30px" }}>
					<v2layouts.ActionButton onClick={onClose} disabled={saving}>
						Cancel
					</v2layouts.ActionButton>
					<v2layouts.ActionButton
						$variant="primary"
						onClick={handleSave}
						disabled={!configuredAs || saving || (configuredAs === "font" && config.font_styles.length === 0)}
					>
						{saving ? "Saving..." : "Save Configuration"}
					</v2layouts.ActionButton>
				</v2layouts.ButtonGroup>
			</v2layouts.ModalContent>
		</v2layouts.Modal>
	)
}

interface ConfigProps {
	config: api.AssetConfigureRequest
	onChange: (config: api.AssetConfigureRequest) => void
}

function LogoConfiguration({ config, onChange }: ConfigProps): JSX.Element {
	return (
		<>
			<v2layouts.FormGroup>
				<v2layouts.FormLabel>Description</v2layouts.FormLabel>
				<v2layouts.Input
					type="text"
					value={config.description}
					onChange={(e) => onChange({ ...config, description: e.target.value })}
					placeholder="e.g., Primary logo for dark backgrounds"
				/>
			</v2layouts.FormGroup>

			<v2layouts.FormGroup>
				<v2layouts.FormLabel>Clearspace Horizontal (%)</v2layouts.FormLabel>
				<v2layouts.Input
					type="number"
					min="0"
					max="100"
					step="0.1"
					value={config.clearspace_horizontal}
					onChange={(e) => onChange({ ...config, clearspace_horizontal: parseFloat(e.target.value) || 0 })}
				/>
				<v2layouts.HelpText>Minimum horizontal clearspace as percentage of logo width</v2layouts.HelpText>
			</v2layouts.FormGroup>

			<v2layouts.FormGroup>
				<v2layouts.FormLabel>Clearspace Vertical (%)</v2layouts.FormLabel>
				<v2layouts.Input
					type="number"
					min="0"
					max="100"
					step="0.1"
					value={config.clearspace_vertical}
					onChange={(e) => onChange({ ...config, clearspace_vertical: parseFloat(e.target.value) || 0 })}
				/>
				<v2layouts.HelpText>Minimum vertical clearspace as percentage of logo height</v2layouts.HelpText>
			</v2layouts.FormGroup>
		</>
	)
}

const FONT_STYLE_OPTIONS = ["regular", "bold", "italic", "light"] as const
type FontStyle = (typeof FONT_STYLE_OPTIONS)[number]

function toggleFontStyle(current: string[], style: FontStyle): string[] {
	const set = new Set(current)
	if (set.has(style)) {
		set.delete(style)
	} else {
		set.add(style)
	}
	return FONT_STYLE_OPTIONS.filter((s) => set.has(s))
}

function FontConfiguration({ config, onChange, asset }: ConfigProps & { asset: api.AssetExtract }): JSX.Element {
	const selected = new Set(config.font_styles)
	return (
		<>
			<v2layouts.FormGroup>
				<v2layouts.FormLabel>Description</v2layouts.FormLabel>
				<v2layouts.Input
					type="text"
					value={config.description}
					onChange={(e) => onChange({ ...config, description: e.target.value })}
					placeholder="e.g., Heading font - Helvetica Neue Bold"
				/>
				<v2layouts.HelpText>Describe the font and its usage (e.g., family, weight, context)</v2layouts.HelpText>
			</v2layouts.FormGroup>

			<v2layouts.FormGroup>
				<v2layouts.FormLabel>Approved styles</v2layouts.FormLabel>
				<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
					{FONT_STYLE_OPTIONS.map((style) => (
						<label key={style} style={{ display: "flex", alignItems: "center", gap: "8px", textTransform: "capitalize", cursor: "pointer" }}>
							<v2layouts.Checkbox
								checked={selected.has(style)}
								onChange={() => onChange({ ...config, font_styles: toggleFontStyle(config.font_styles, style) })}
							/>
							{style}
						</label>
					))}
				</div>
				<v2layouts.HelpText>At least one style must be selected. Assets using the approved font family in a non-selected style will be flagged as non-compliant.</v2layouts.HelpText>
			</v2layouts.FormGroup>
		</>
	)
}

function ColorConfiguration({ config, onChange, asset }: ConfigProps & { asset: api.AssetExtract }): JSX.Element {
	return (
		<>
			<v2layouts.FormGroup>
				<v2layouts.FormLabel>Hex Code</v2layouts.FormLabel>
				<v2layouts.Input
					type="text"
					value={config.color}
					onChange={(e) => onChange({ ...config, color: e.target.value })}
					placeholder="#0000FF"
				/>
			</v2layouts.FormGroup>

			<v2layouts.FormGroup>
				<v2layouts.FormLabel>Description</v2layouts.FormLabel>
				<v2layouts.Input
					type="text"
					value={config.description}
					onChange={(e) => onChange({ ...config, description: e.target.value })}
					placeholder="e.g., Primary Blue - used for CTAs and headers"
				/>
			</v2layouts.FormGroup>
		</>
	)
}

function LookAndFeelConfiguration({ config, onChange }: ConfigProps): JSX.Element {
	return (
		<>
			<v2layouts.FormGroup>
				<v2layouts.FormLabel>Description</v2layouts.FormLabel>
				<v2layouts.Input
					type="text"
					value={config.description}
					onChange={(e) => onChange({ ...config, description: e.target.value })}
					placeholder="e.g., Modern lifestyle photography"
				/>
			</v2layouts.FormGroup>

			<v2layouts.FormGroup>
				<v2layouts.FormLabel>Rule</v2layouts.FormLabel>
				<v2layouts.Input
					type="text"
					value={config.rule}
					onChange={(e) => onChange({ ...config, rule: e.target.value })}
					placeholder="e.g., Use authentic, lifestyle-focused imagery with natural lighting"
				/>
				<v2layouts.HelpText>Describe the visual style and guidelines</v2layouts.HelpText>
			</v2layouts.FormGroup>
		</>
	)
}

function ToneOfVoiceConfiguration({ config, onChange }: ConfigProps): JSX.Element {
	return (
		<>
			<v2layouts.FormGroup>
				<v2layouts.FormLabel>Description</v2layouts.FormLabel>
				<v2layouts.Input
					type="text"
					value={config.description}
					onChange={(e) => onChange({ ...config, description: e.target.value })}
					placeholder="Describe the tone and voice"
				/>
			</v2layouts.FormGroup>
		</>
	)
}
